/**
 * Badge — Small visual indicator for status, counts, or icons beside other elements.
 *
 * Badge Component Guidelines
 * - Use Badge for compact, non-interactive indicators (e.g. notification count, icon
 *   container, status dot). For interactive labels with semantic color, use Tag instead.
 * - Size should match the adjacent element's scale: `xs`/`sm` beside text or in dense
 *   lists, `base`/`lg` beside headings or cards, `xl` for standalone prominent indicators.
 * - Use the `render` prop to change the underlying element (e.g. render as a `<div>`)
 *   when Badge needs to be a non-`<span>` host.
 */
import type { VariantProps } from "class-variance-authority";

import * as React from "react";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import twMerge from "@replo/design-system/utils/twMerge";
import { cva } from "class-variance-authority";

const badgeVariants = cva(
  "bg-white font-medium inline-flex items-center justify-center shrink-0 overflow-hidden shadow-2xs border border-0.5 border-border",
  {
    variants: {
      size: {
        xs: "size-3 rounded-sm text-2xs [&>svg]:size-1.5",
        sm: "size-4 rounded-sm text-xs [&>svg]:size-2",
        base: "size-5 rounded-md text-xs [&>svg]:size-2.5",
        lg: "size-6 rounded-md text-xs [&>svg]:size-3",
        xl: "size-8 rounded-lg text-sm [&>svg]:size-4",
      },
    },
    defaultVariants: {
      size: "base",
    },
  },
);

type BadgeSize = VariantProps<typeof badgeVariants>["size"];

function Badge({
  className,
  size = "base",
  render,
  ...props
}: useRender.ComponentProps<"span"> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        className: twMerge(badgeVariants({ size }), className),
      },
      props,
    ),
    render,
    state: {
      slot: "badge",
      size,
    },
  });
}

export { Badge, type BadgeSize };
